/**
 * responseStore - 回應階段狀態管理
 * 獨立的 store，管理回應階段的所有狀態
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { CardDefinition, GameState } from '../types';
import type { ResponseOption, ChainEntry, ResponseTriggerType } from '../types/response';
import type { ResponsePhaseState, ResponsePhaseConfig } from '../core/response';
import {
  ResponsePhaseController,
  AIResponseDecider,
  AI_PERSONALITIES,
} from '../core/response';
import type { ImmediateAction } from '../core/effects/EffectTriggerSystem';
import { getLogger } from '../utils/logger';

const log = getLogger('responseStore');

// ============================================================
// Store 類型定義
// ============================================================

interface ResponseStore {
  // State
  controller: ResponsePhaseController | null;
  aiDecider: AIResponseDecider | null;
  phaseState: ResponsePhaseState | null;
  chainEntries: ChainEntry[];
  isResolving: boolean;
  
  // Config
  aiPlayerId: string | null;
  localPlayerId: string | null;

  // Actions - 初始化
  initialize: (
    cardDefinitions: Map<string, CardDefinition>,
    localPlayerId: string,
    aiPlayerId: string,
    config?: Partial<ResponsePhaseConfig>
  ) => void;

  // Actions - 回應階段控制
  startResponsePhase: (
    gameState: GameState,
    triggerType: ResponseTriggerType,
    triggerPlayerId: string,
    triggerCardId: string,
    triggerCardName: string,
    effectText: string,
    actions: ImmediateAction[],
    targets?: string[]
  ) => Promise<boolean>;

  respond: (
    gameState: GameState,
    response: ResponseOption,
    targets?: string[]
  ) => Promise<boolean>;

  pass: () => Promise<boolean>;

  resolveChain: (
    gameState: GameState,
    executeAction: (state: GameState, action: ImmediateAction) => GameState
  ) => GameState;

  forceEnd: () => void;

  // Actions - 狀態更新
  updatePhaseState: (state: Partial<ResponsePhaseState>) => void;
  addChainEntry: (entry: ChainEntry) => void;
  clearChain: () => void;

  // Getters
  isActive: () => boolean;
  isMyTurn: () => boolean;
  getAvailableResponses: () => ResponseOption[];
}

// ============================================================
// 初始狀態
// ============================================================

const initialPhaseState: ResponsePhaseState = {
  isActive: false,
  triggerType: null,
  triggerCardId: null,
  triggerCardName: null,
  triggerPlayerId: null,
  currentPriority: null,
  availableResponses: [],
  timeRemaining: null,
  startTime: null,
  consecutivePasses: 0,
  waitingForAI: false,
};

// ============================================================
// Store 實作
// ============================================================

export const useResponseStore = create<ResponseStore>()(
  devtools(
    (set, get) => ({
      // Initial State
      controller: null,
      aiDecider: null,
      phaseState: null,
      chainEntries: [],
      isResolving: false,
      aiPlayerId: null,
      localPlayerId: null,

      // 初始化
      initialize: (cardDefinitions, localPlayerId, aiPlayerId, config) => {
        const controller = new ResponsePhaseController(cardDefinitions, {
          enableTimer: false, // 單機版不需要計時
          ...config,
        });

        const aiDecider = new AIResponseDecider(
          AI_PERSONALITIES.balanced,
          cardDefinitions
        );

        controller.setAIPlayer(aiPlayerId);

        // 監聽事件
        controller.on((event) => {
          const store = get();

          switch (event.type) {
            case 'phase_start':
              set({
                phaseState: controller.getState(),
                chainEntries: [],
              });
              break;

            case 'phase_end':
              set({
                phaseState: { ...initialPhaseState },
                chainEntries: [],
                isResolving: false,
              });
              break;

            case 'priority_change':
              set({
                phaseState: controller.getState(),
              });
              break;

            case 'response_received':
              if (event.chainEntry) {
                set({
                  chainEntries: [...store.chainEntries, event.chainEntry],
                  phaseState: controller.getState(),
                });
              }
              break;

            case 'pass_received':
              set({
                phaseState: controller.getState(),
              });
              break;

            case 'chain_resolve_start':
              set({ isResolving: true });
              break;

            case 'chain_resolve_end':
              set({ isResolving: false });
              break;
          }
        });

        set({
          controller,
          aiDecider,
          localPlayerId,
          aiPlayerId,
          phaseState: { ...initialPhaseState },
        });

        log.debug('初始化完成', { localPlayerId, aiPlayerId });
      },

      // 開始回應階段
      startResponsePhase: async (
        gameState,
        triggerType,
        triggerPlayerId,
        triggerCardId,
        triggerCardName,
        effectText,
        actions,
        targets
      ) => {
        const { controller } = get();
        if (!controller) {
          log.error('Controller 未初始化');
          return false;
        }

        try {
          const result = await controller.startResponsePhase(
            gameState,
            triggerType,
            triggerPlayerId,
            triggerCardId,
            triggerCardName,
            effectText,
            actions,
            targets
          );

          return result.responded;
        } catch (error) {
          log.error('startResponsePhase 錯誤:', error);
          return false;
        }
      },

      // 玩家回應
      respond: async (gameState, response, targets) => {
        const { controller, localPlayerId } = get();
        if (!controller || !localPlayerId) {
          log.error('Controller 或 localPlayerId 未設定');
          return false;
        }

        try {
          const result = await controller.respond(
            gameState,
            localPlayerId,
            response,
            targets
          );

          return result.responded;
        } catch (error) {
          log.error('respond 錯誤:', error);
          return false;
        }
      },

      // 玩家放棄回應
      pass: () => {
        const { controller, localPlayerId } = get();
        if (!controller || !localPlayerId) {
          return Promise.resolve(false);
        }

        try {
          controller.pass(localPlayerId);
          return Promise.resolve(true);
        } catch (error) {
          log.error('pass 錯誤:', error);
          return Promise.resolve(false);
        }
      },

      // 結算連鎖
      resolveChain: (gameState, executeAction) => {
        const { controller } = get();
        if (!controller) {
          return gameState;
        }

        return controller.resolveChain(gameState, executeAction);
      },

      // 強制結束
      forceEnd: () => {
        const { controller } = get();
        if (controller) {
          controller.forceEnd();
        }
        set({
          phaseState: { ...initialPhaseState },
          chainEntries: [],
          isResolving: false,
        });
      },

      // 更新狀態
      updatePhaseState: (state) => {
        set((prev) => ({
          phaseState: prev.phaseState
            ? { ...prev.phaseState, ...state }
            : { ...initialPhaseState, ...state },
        }));
      },

      // 新增連鎖項目
      addChainEntry: (entry) => {
        set((prev) => ({
          chainEntries: [...prev.chainEntries, entry],
        }));
      },

      // 清空連鎖
      clearChain: () => {
        set({ chainEntries: [] });
      },

      // Getters
      isActive: () => {
        const { phaseState } = get();
        return phaseState?.isActive ?? false;
      },

      isMyTurn: () => {
        const { phaseState, localPlayerId } = get();
        return phaseState?.currentPriority === localPlayerId;
      },

      getAvailableResponses: () => {
        const { phaseState } = get();
        return phaseState?.availableResponses ?? [];
      },
    }),
    { name: 'response-store' }
  )
);

// ============================================================
// Selectors
// ============================================================

export const selectResponsePhaseState = (state: ResponseStore) => state.phaseState;
export const selectChainEntries = (state: ResponseStore) => state.chainEntries;
export const selectIsResolving = (state: ResponseStore) => state.isResolving;
export const selectIsActive = (state: ResponseStore) => state.phaseState?.isActive ?? false;
export const selectIsMyTurn = (state: ResponseStore) => {
  const { phaseState, localPlayerId } = state;
  return phaseState?.currentPriority === localPlayerId;
};
